import { supabase } from './supabaseService';
import { serviceConnectionsService } from './serviceConnectionsService';
import { logger } from '../utils/logger';

export interface CalendlyEvent {
  id: string;
  calendly_event_uri: string;
  event_type_name: string;
  event_name: string | null;
  status: 'active' | 'canceled';
  start_time: string;
  end_time: string;
  invitee_name: string | null;
  invitee_email: string | null;
  location_type: string | null;
  location_url: string | null;
  questions_and_answers: { question: string; answer: string }[];
  cancel_reason: string | null;
  contact_id: string | null;
  lead_id: string | null;
  activity_id: string | null;
  created_at: string;
}

export interface CalendlyConfig {
  apiKey: string;
  webhookUrl: string;
  isConfigured: boolean;
  schedulingUrl: string; // e.g. https://calendly.com/ceroideas
}

class CalendlyService {
  private config: CalendlyConfig | null = null;

  async loadConfig(): Promise<CalendlyConfig | null> {
    try {
      const connection = await serviceConnectionsService.getServiceConnection('calendly');
      if (!connection) return null;

      this.config = {
        apiKey: connection.apiKey || '',
        webhookUrl: connection.configData?.webhookUrl || '',
        isConfigured: connection.isActive && !!connection.apiKey,
        schedulingUrl: connection.configData?.schedulingUrl || '',
      };
      return this.config;
    } catch (error) {
      logger.error('Error loading Calendly config', error);
      return null;
    }
  }

  async saveConfig(config: Partial<CalendlyConfig>): Promise<void> {
    await serviceConnectionsService.saveServiceConnection(
      'calendly',
      config.apiKey || this.config?.apiKey || '',
      {
        webhookUrl: config.webhookUrl || this.config?.webhookUrl,
        schedulingUrl: config.schedulingUrl || this.config?.schedulingUrl,
        isConfigured: true,
      }
    );
    this.config = { ...this.config, ...config } as CalendlyConfig;
  }

  isConfigured(): boolean {
    return !!(this.config?.apiKey && this.config.isConfigured);
  }

  // === Events from DB (populated by webhooks) ===

  async getUpcomingEvents(limit = 20): Promise<CalendlyEvent[]> {
    const { data, error } = await supabase
      .from('calendly_events')
      .select('*')
      .eq('status', 'active')
      .gte('start_time', new Date().toISOString())
      .order('start_time', { ascending: true })
      .limit(limit);

    if (error) { logger.error('Error fetching upcoming events', error); return []; }
    return data || [];
  }

  async getPastEvents(limit = 20): Promise<CalendlyEvent[]> {
    const { data, error } = await supabase
      .from('calendly_events')
      .select('*')
      .lt('start_time', new Date().toISOString())
      .order('start_time', { ascending: false })
      .limit(limit);

    if (error) { logger.error('Error fetching past events', error); return []; }
    return data || [];
  }

  async getAllEvents(limit = 50): Promise<CalendlyEvent[]> {
    const { data, error } = await supabase
      .from('calendly_events')
      .select('*')
      .order('start_time', { ascending: false })
      .limit(limit);

    if (error) { logger.error('Error fetching all events', error); return []; }
    return data || [];
  }

  /** Events strictly within [start, end). Used by InternalCalendar para no
   *  truncar silenciosamente cuando hay más de N events en el mes visible. */
  async getEventsInRange(start: Date, end: Date): Promise<CalendlyEvent[]> {
    const { data, error } = await supabase
      .from('calendly_events')
      .select('*')
      .gte('start_time', start.toISOString())
      .lt('start_time', end.toISOString())
      .order('start_time', { ascending: true });

    if (error) { logger.error('Error fetching events in range', error); return []; }
    return data || [];
  }

  async getEventsByContact(contactId: string): Promise<CalendlyEvent[]> {
    const { data, error } = await supabase
      .from('calendly_events')
      .select('*')
      .eq('contact_id', contactId)
      .order('start_time', { ascending: false });

    if (error) { logger.error('Error fetching contact events', error); return []; }
    return data || [];
  }

  /** Link a Calendly event to a CRM contact */
  async linkToContact(eventId: string, contactId: string): Promise<void> {
    const { error } = await supabase
      .from('calendly_events')
      .update({ contact_id: contactId })
      .eq('id', eventId);

    if (error) throw error;
  }

  /** Link a Calendly event to a CRM lead */
  async linkToLead(eventId: string, leadId: string): Promise<void> {
    const { error } = await supabase
      .from('calendly_events')
      .update({ lead_id: leadId })
      .eq('id', eventId);

    if (error) throw error;
  }

  // === API calls via Calendly API (for fetching events on demand) ===

  async testConnection(): Promise<boolean> {
    if (!this.config?.apiKey) throw new Error('API Key de Calendly no configurada');

    const response = await fetch('https://api.calendly.com/users/me', {
      headers: {
        'Authorization': `Bearer ${this.config.apiKey}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      if (response.status === 401) throw new Error('API Key inválida');
      throw new Error(`Error ${response.status}`);
    }

    const data = await response.json();
    logger.info('Calendly connected:', data.resource?.name);
    return true;
  }

  /** Fetch scheduled events from Calendly API and sync to DB */
  async syncEvents(): Promise<number> {
    if (!this.config?.apiKey) throw new Error('Calendly no configurada');

    // Get current user URI
    const userRes = await fetch('https://api.calendly.com/users/me', {
      headers: { 'Authorization': `Bearer ${this.config.apiKey}` },
    });

    if (!userRes.ok) throw new Error('Error conectando con Calendly');
    const userData = await userRes.json();
    const userUri = userData.resource?.uri;

    if (!userUri) throw new Error('No se pudo obtener URI del usuario');

    // Fetch scheduled events
    const now = new Date();
    const monthAgo = new Date(now.getTime() - 30 * 86400000);

    const eventsRes = await fetch(
      `https://api.calendly.com/scheduled_events?user=${encodeURIComponent(userUri)}&min_start_time=${monthAgo.toISOString()}&max_start_time=${new Date(now.getTime() + 60 * 86400000).toISOString()}&count=100`,
      { headers: { 'Authorization': `Bearer ${this.config.apiKey}` } }
    );

    if (!eventsRes.ok) throw new Error('Error obteniendo eventos');
    const eventsData = await eventsRes.json();
    const events = eventsData.collection || [];

    let synced = 0;

    for (const ev of events) {
      // Get invitees for each event
      const inviteesRes = await fetch(
        `${ev.uri}/invitees?count=10`,
        { headers: { 'Authorization': `Bearer ${this.config.apiKey}` } }
      );

      let invitees: any[] = [];
      if (inviteesRes.ok) {
        const invData = await inviteesRes.json();
        invitees = invData.collection || [];
      }

      const primaryInvitee = invitees[0];

      // Auto-match contact
      let contactId = null;
      let leadId = null;
      if (primaryInvitee?.email) {
        const { data: contact } = await supabase
          .from('contacts')
          .select('id')
          .ilike('email', primaryInvitee.email)
          .limit(1)
          .maybeSingle();

        if (contact) {
          contactId = contact.id;
        } else {
          const { data: lead } = await supabase
            .from('leads')
            .select('id')
            .ilike('email', primaryInvitee.email)
            .limit(1)
            .maybeSingle();
          if (lead) leadId = lead.id;
        }
      }

      const { error } = await supabase
        .from('calendly_events')
        .upsert({
          calendly_event_uri: ev.uri,
          calendly_invitee_uri: primaryInvitee?.uri || null,
          event_type_name: ev.event_type || ev.name || 'Reunión',
          event_name: ev.name || null,
          status: ev.status === 'canceled' ? 'canceled' : 'active',
          start_time: ev.start_time,
          end_time: ev.end_time,
          invitee_name: primaryInvitee?.name || null,
          invitee_email: primaryInvitee?.email || null,
          location_type: ev.location?.type || null,
          location_url: ev.location?.join_url || null,
          questions_and_answers: primaryInvitee?.questions_and_answers || [],
          contact_id: contactId,
          lead_id: leadId,
          raw_payload: ev,
        }, {
          onConflict: 'calendly_event_uri',
        });

      if (!error) synced++;
    }

    logger.info(`Calendly sync: ${synced} events synced`);
    return synced;
  }
}

export const calendlyService = new CalendlyService();
